import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { DSSetup, RecordAttributes, RecordData } from 'webapp-types';
import { Scale, ShownLabels } from 'webapp/enums/enums';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type DataSelectRecord from 'webapp/models/data-select';

export default class DataSelectSerializer extends ApplicationSerializer {
	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.DataSelect.Raw | RecordData.DataSelect.Serialized,
		prop: string | undefined,
	) {
		const { metricSetup, filters, config, ...data } = json;
		const attributes: Partial<RecordAttributes<DataSelectRecord>> & { id?: string | number } = {
			...data,
		};

		// remove auto scale in case it's sent
		if (attributes.overrides?.scale === Scale.AUTO_BE) {
			delete attributes.overrides.scale;
		}

		if (metricSetup) {
			const normalizedMetricSetup = this.normalizeMetricSetupData(
				metricSetup as DSSetup.MetricSetupData,
			)!;
			attributes.metricSetupKey = normalizedMetricSetup.key;
			attributes.metricSetupSpaceAccessId = normalizedMetricSetup.spaceAccessId;
			attributes.metricSetupDimensions = normalizedMetricSetup.dimensions || [];
			attributes.metricSetupFilters = normalizedMetricSetup.filters || null;
		}

		// FILTERS
		// ATTENTION! when adding new values to filters, config or overrides, BE MUST BE INFORMED
		// BE has some filtering and needs to add all props to whitelist
		attributes.sortDataSelect = filters?.sortDataSelect || null;
		attributes.recordLimit = filters?.recordLimit || null;

		// CONFIG
		attributes.skipAutoUpdate = !!config?.skipAutoUpdate;
		attributes.constValue = typeof config?.constValue === 'number' ? config.constValue : null;
		attributes.visualization = config?.visualization || null;
		attributes.isStacked = !!config?.isStacked;
		attributes.aggregation = config?.aggregation || null;
		attributes.shownLabels = config?.shownLabels ? config.shownLabels : ShownLabels.NONE;
		attributes.yAxisIndex = typeof config?.yAxisIndex === 'number' ? config.yAxisIndex : null;
		attributes.startValue = typeof config?.startValue === 'number' ? config.startValue : null;
		attributes.order = typeof config?.order === 'number' ? config.order : 0;
		attributes.anomalySensitivity =
			typeof config?.anomaly?.sensitivity === 'number' ? config.anomaly.sensitivity : null;
		attributes.anomalyTrainingPeriod =
			typeof config?.anomaly?.trainingPeriod === 'number'
				? config.anomaly.trainingPeriod
				: null;
		attributes.anomalyAdaptability =
			typeof config?.anomaly?.adaptability === 'string' ? config.anomaly.adaptability : null;

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.DataSelect.Serialized {
		const attributes = super.serialize(snapshot, options) as RecordAttributes<DataSelectRecord>;

		// remove auto scale in case we have it here
		// (the transforms pass the record's objects through, so this also changes the record's overrides)
		if (attributes.overrides?.scale === Scale.AUTO) {
			delete attributes.overrides.scale;
		}

		// metricKey is a webapp-only key (also removed from the record's compares, see above)
		(attributes.compares || []).forEach((compare) => {
			delete (compare as { metricKey?: string }).metricKey;
		});

		// key is null for data selects without a metric
		const metricSetup = this.serializeMetricSetupData({
			key: attributes.metricSetupKey,
			spaceAccessId: attributes.metricSetupSpaceAccessId,
			dimensions: attributes.metricSetupDimensions,
			filters: attributes.metricSetupFilters,
		} as DSSetup.MetricSetupData);

		// FILTERS
		const filters: RecordData.DataSelect.Filters = {};
		if (attributes.sortDataSelect) {
			filters.sortDataSelect = attributes.sortDataSelect;
		}
		if (attributes.recordLimit) {
			filters.recordLimit = attributes.recordLimit;
		}

		// CONFIG
		const config: RecordData.DataSelect.Config = { order: attributes.order || 0 };
		if (attributes.skipAutoUpdate) {
			config.skipAutoUpdate = attributes.skipAutoUpdate;
		}
		if (typeof attributes.constValue === 'number') {
			config.constValue = attributes.constValue;
		}
		if (attributes.visualization) {
			config.visualization = attributes.visualization;
		}
		if (attributes.isStacked) {
			config.isStacked = attributes.isStacked;
		}
		if (attributes.aggregation) {
			config.aggregation = attributes.aggregation;
		}
		if (attributes.shownLabels) {
			config.shownLabels = attributes.shownLabels;
		}
		if (typeof attributes.yAxisIndex === 'number') {
			config.yAxisIndex = attributes.yAxisIndex;
		}
		if (typeof attributes.startValue === 'number') {
			config.startValue = attributes.startValue;
		}
		if (
			typeof attributes.anomalySensitivity === 'number' &&
			typeof attributes.anomalyTrainingPeriod === 'number' &&
			typeof attributes.anomalyAdaptability === 'string'
		) {
			config.anomaly = {
				sensitivity: attributes.anomalySensitivity,
				trainingPeriod: attributes.anomalyTrainingPeriod,
				adaptability: attributes.anomalyAdaptability,
			};
		}

		return {
			...omit(
				attributes,
				// moved to metricSetup
				'metricSetupKey',
				'metricSetupSpaceAccessId',
				'metricSetupDimensions',
				'metricSetupFilters',
				// moved to filters
				'sortDataSelect',
				'recordLimit',
				// moved to config
				'skipAutoUpdate',
				'constValue',
				'visualization',
				'isStacked',
				'aggregation',
				'shownLabels',
				'yAxisIndex',
				'startValue',
				'order',
				'anomalySensitivity',
				'anomalyTrainingPeriod',
				'anomalyAdaptability',
			),
			metricSetup,
			filters,
			config,
		};
	}
}
